const Street_Suffixes = new Set([
  "AVE",
  "AV",
  "AVENUE",
  "ST",
  "STREET",
  "RD",
  "ROAD",
  "DR",
  "DRIVE",
  "LN",
  "LANE",
  "CT",
  "COURT",
  "BLVD",
  "BOULEVARD",
  "WAY",
  "PL",
  "PLACE",
  "CIR",
  "CIRCLE",
]);

const Po_Box_Re = /\bP\.?\s*O\.?\s*BOX\b/i;
const Mailing_Only_Re = /\b(LOCKBOX|PSC|PMB|PRIVATE\s+MAIL\s+BOX)\b/i;

export function Address_Is_PoBox(Text: string): boolean {
  const Value = (Text || "").trim();
  if (!Value) {
    return false;
  }
  return Po_Box_Re.test(Value) || Mailing_Only_Re.test(Value);
}

/** True when address line starts with a house number (e.g. 176, 176A). */
export function Address_Street_Number_Has(Text: string): boolean {
  const First = (Text || "").trim().split(/\s+/)[0] || "";
  return /^\d+[A-Za-z]?$/.test(First);
}

export function Address_Situs_Format(
  Street: string,
  City = "",
  State = "CA",
  Zip_Code = "",
): string {
  const Street_Trimmed = (Street || "").trim();
  if (!Street_Trimmed || Address_Is_PoBox(Street_Trimmed)) {
    return "";
  }

  const Upper = Street_Trimmed.toUpperCase();
  const Parts: string[] = [Street_Trimmed];
  const City_Trimmed = (City || "").trim();
  if (City_Trimmed && !Upper.includes(City_Trimmed.toUpperCase())) {
    Parts.push(City_Trimmed);
  }

  const State_Zip = [((State || "CA").trim()), (Zip_Code || "").trim()]
    .filter(Boolean)
    .join(" ");
  if (State_Zip && !Upper.includes(State_Zip.toUpperCase())) {
    Parts.push(State_Zip);
  }

  return Parts.join(", ");
}

export type Parsed_Mailing_Address = {
  street_number: string;
  street_name: string;
  suffix: string;
  /** Condo / apt / suite designator when present (e.g. A, 4, 12B). */
  unit: string;
  city: string;
  state: string;
  zip: string;
  street_line: string;
};

const Unit_Token_Re =
  /^(?:#|APT\.?|APARTMENT|UNIT|STE\.?|SUITE|NO\.?)$/i;

/** Pull trailing unit designator off street tokens (condo / apt / suite). */
export function Address_Unit_Tokens_Strip(Tokens: string[]): {
  Tokens: string[];
  Unit: string;
} {
  if (Tokens.length < 2) {
    return { Tokens, Unit: "" };
  }

  const Last = Tokens[Tokens.length - 1] || "";
  const Prev = Tokens[Tokens.length - 2] || "";

  if (Unit_Token_Re.test(Prev) && /^[A-Za-z0-9\-]+$/.test(Last)) {
    return { Tokens: Tokens.slice(0, -2), Unit: Last.toUpperCase() };
  }

  const Hash_Match = Last.match(/^#\s*([A-Za-z0-9\-]+)$/);
  if (Hash_Match) {
    return { Tokens: Tokens.slice(0, -1), Unit: Hash_Match[1].toUpperCase() };
  }

  return { Tokens, Unit: "" };
}

function Looks_Like_City_Name(Name: string): boolean {
  const Words = Name.split(/\s+/).filter(Boolean);
  if (!Words.length || Name.trim().length < 3) {
    return false;
  }
  for (const Word of Words) {
    if (Word.length < 2) {
      return false;
    }
    const Token = Word.toUpperCase().replace(/\.$/, "");
    if (Street_Suffixes.has(Token)) {
      return false;
    }
    if (!/^[A-Za-z'-]+$/.test(Word)) {
      return false;
    }
  }
  return true;
}

/** Best-effort parse of a one-line US address for statewide ArcGIS lookup. */
export function Addresses_Mailing_Parse(Line: string): Parsed_Mailing_Address {
  let Text = Line.replace(/,/g, " ").replace(/\s+/g, " ").trim();

  let Zip = "";
  const Zip_Match = Text.match(/\b(\d{5})(?:-\d{4})?\s*$/);
  if (Zip_Match) {
    Zip = Zip_Match[1];
    Text = Text.slice(0, Zip_Match.index).trim();
  }

  let State = "";
  const State_Match = Text.match(/\b(CA|California)\s*$/i);
  if (State_Match) {
    State = "CA";
    Text = Text.slice(0, State_Match.index).trim();
  }

  const Tokens = Text.split(/\s+/).filter(Boolean);
  let City = "";
  let Street_Tokens = Tokens;

  if (Tokens.length >= 2) {
    for (const N of [3, 2, 1]) {
      if (Tokens.length <= N) {
        continue;
      }
      const City_Candidate = Tokens.slice(-N).join(" ");
      if (Looks_Like_City_Name(City_Candidate)) {
        City = City_Candidate.toUpperCase();
        Street_Tokens = Tokens.slice(0, -N);
        break;
      }
    }
  }

  const Unit_Stripped = Address_Unit_Tokens_Strip(Street_Tokens);
  Street_Tokens = Unit_Stripped.Tokens;
  const Unit = Unit_Stripped.Unit;

  let Suffix = "";
  if (Street_Tokens.length) {
    const Last = Street_Tokens[Street_Tokens.length - 1].toUpperCase().replace(/\.$/, "");
    if (Street_Suffixes.has(Last)) {
      Suffix = Last;
      Street_Tokens = Street_Tokens.slice(0, -1);
    }
  }

  const Street_Number =
    Street_Tokens.length && /^\d+$/.test(Street_Tokens[0]) ? Street_Tokens[0] : "";
  const Street_Name = (Street_Number ? Street_Tokens.slice(1) : Street_Tokens).join(" ").trim();
  const Street_Line = [Street_Number, Street_Name, Suffix].filter(Boolean).join(" ").trim();

  return {
    street_number: Street_Number,
    street_name: Street_Name,
    suffix: Suffix,
    unit: Unit,
    city: City,
    state: State || "CA",
    zip: Zip,
    street_line: Street_Line,
  };
}
